// =====================================================
// 产品矩阵管理系统 - 性能优化 Hooks
// 提供缓存、节流等性能优化功能
// =====================================================

import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import throttle from 'lodash.throttle';
import { useCanvasStore } from '../stores/canvas-store';
import { useProductStore } from '../stores/product-store';
import type { Product, CanvasCell } from '../types';
import { DRAG_CONFIG, LAYOUT_CONFIGS, CUSTOM_LAYOUT_CONFIG } from '../constants';

// =====================================================
// 产品缓存 Hook
// =====================================================

/**
 * 使用产品 Map 缓存，实现 O(1) 查找
 */
export function useProductCache() {
  const { allProducts, products } = useProductStore();
  
  // 创建产品 Map 缓存
  const productMap = useMemo(() => {
    const map = new Map<string, Product>();
    
    // 优先使用 allProducts（完整列表）
    const sourceProducts = allProducts.length > 0 ? allProducts : products;
    
    sourceProducts.forEach(product => {
      map.set(product.id, product);
    });
    
    return map;
  }, [allProducts, products]);
  
  // 通过 ID 获取产品 - O(1)
  const getProductById = useCallback((id: string | undefined): Product | undefined => {
    if (!id) return undefined;
    return productMap.get(id);
  }, [productMap]);
  
  // 批量获取产品
  const getProductsByIds = useCallback((ids: (string | undefined)[]): (Product | undefined)[] => {
    return ids.map(id => id ? productMap.get(id) : undefined);
  }, [productMap]);
  
  return {
    productMap,
    getProductById,
    getProductsByIds,
    productCount: productMap.size,
  };
}

// =====================================================
// 单元格渲染缓存 Hook
// =====================================================

/**
 * 缓存单元格的产品数据，避免重复查找
 */
export function useCellProductCache() {
  const { canvasData } = useCanvasStore();
  const { getProductById } = useProductCache();
  
  // 缓存单元格与产品的映射
  const cellProductMap = useMemo(() => {
    return canvasData.cells.map(cell => ({
      ...cell,
      product: cell.product_id ? getProductById(cell.product_id) : undefined,
    }));
  }, [canvasData.cells, getProductById]);
  
  return cellProductMap;
}

// =====================================================
// 网格计算缓存 Hook
// =====================================================

interface GridCalculation {
  rows: number;
  cols: number;
  gapRatio: number;
  cellCount: number;
  gridSize: number;
  gapSize: number;
  cellSize: number;
}

/**
 * 缓存网格尺寸计算
 */
export function useGridCalculation(containerWidth: number, containerHeight: number) {
  const { layoutType, customRows, customCols } = useCanvasStore();
  
  const calculation = useMemo<GridCalculation>(() => {
    // 获取布局配置
    let config: { rows: number; cols: number; gapRatio: number };
    
    if (layoutType === 'grid_custom') {
      config = {
        rows: customRows,
        cols: customCols,
        gapRatio: CUSTOM_LAYOUT_CONFIG.defaultGapRatio,
      };
    } else {
      config = LAYOUT_CONFIGS[layoutType];
    }
    
    const { rows, cols, gapRatio } = config;
    const cellCount = rows * cols;
    
    // 计算网格尺寸
    const gridSize = Math.min(containerWidth, containerHeight, 800);
    const gapSize = Math.round(gridSize * gapRatio);
    const totalGapWidth = gapSize * (cols - 1);
    const totalGapHeight = gapSize * (rows - 1);
    
    // 计算单元格尺寸
    const cellWidth = Math.floor((gridSize - totalGapWidth) / cols);
    const cellHeight = Math.floor((gridSize - totalGapHeight) / rows);
    const cellSize = Math.min(cellWidth, cellHeight);
    
    return {
      rows,
      cols,
      gapRatio,
      cellCount,
      gridSize,
      gapSize,
      cellSize,
    };
  }, [layoutType, customRows, customCols, containerWidth, containerHeight]);
  
  return calculation;
}

// =====================================================
// 网格样式缓存 Hook
// =====================================================

/**
 * 缓存 CSS Grid 样式
 */
export function useGridStyle(calculation: GridCalculation) {
  const gridStyle = useMemo(() => ({
    gridTemplateColumns: `repeat(${calculation.cols}, ${calculation.cellSize}px)`,
    gridTemplateRows: `repeat(${calculation.rows}, ${calculation.cellSize}px)`,
    gap: `${calculation.gapSize}px`,
  }), [calculation.cols, calculation.rows, calculation.cellSize, calculation.gapSize]);
  
  return gridStyle;
}

// =====================================================
// 拖拽节流 Hook
// =====================================================

/**
 * 节流拖拽事件处理
 */
export function useThrottledDragHandler<T extends (...args: any[]) => void>(
  handler: T,
  interval = DRAG_CONFIG.throttleInterval
): T {
  const throttledHandler = useMemo(
    () => throttle(handler, interval, { leading: true, trailing: true }),
    [handler, interval]
  );
  
  // 清理
  useEffect(() => {
    return () => {
      throttledHandler.cancel();
    };
  }, [throttledHandler]);
  
  return throttledHandler as T;
}

// =====================================================
// ResizeObserver Hook
// =====================================================

interface Size {
  width: number;
  height: number;
}

/**
 * 监听元素尺寸变化，带节流
 */
export function useResizeObserver(
  ref: React.RefObject<HTMLElement>,
  callback?: (size: Size) => void
): Size {
  const sizeRef = useRef<Size>({ width: 0, height: 0 });
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  
  useEffect(() => {
    if (!ref.current) return;
    
    const updateSize = throttle(() => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const newSize = {
          width: rect.width,
          height: rect.height,
        };
        
        if (newSize.width !== sizeRef.current.width || 
            newSize.height !== sizeRef.current.height) {
          sizeRef.current = newSize;
          setSize(newSize);
          callback?.(newSize);
        }
      }
    }, 100);
    
    // 初始化
    updateSize();
    
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(ref.current);
    
    return () => {
      updateSize.cancel();
      resizeObserver.disconnect();
    };
  }, [ref, callback]);
  
  return size;
}

// =====================================================
// 单元格选择缓存 Hook
// =====================================================

/**
 * 缓存选中状态判断
 */
export function useSelectedCellCache() {
  const { selectedCellIndex } = useCanvasStore();
  
  const isSelected = useCallback((index: number) => {
    return selectedCellIndex === index;
  }, [selectedCellIndex]);
  
  return { selectedCellIndex, isSelected };
}

// =====================================================
// 组合优化 Hook
// =====================================================

/**
 * 组合所有优化功能的 Hook
 */
export function useOptimizedCanvas(
  containerRef: React.RefObject<HTMLElement>
) {
  // 容器尺寸
  const containerSize = useResizeObserver(containerRef);
  
  // 网格计算
  const gridCalculation = useGridCalculation(
    containerSize.width,
    containerSize.height
  );
  
  // 网格样式
  const gridStyle = useGridStyle(gridCalculation);
  
  // 单元格产品缓存
  const cellProductMap = useCellProductCache();
  
  // 选中状态缓存
  const { selectedCellIndex, isSelected } = useSelectedCellCache();
  
  return {
    containerSize,
    gridCalculation,
    gridStyle,
    cellProductMap,
    selectedCellIndex,
    isSelected,
  };
}

export default useOptimizedCanvas;
